/**
 * @since 2025-01-26
 * @author Claude Code
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import * as React from 'react';
import { useState, useEffect } from 'react';

export interface LoginComponentProps {
  /** Application title to display */
  title?: string;
  /** Logo URL */
  logoUrl?: string;
  /** Whether Firebase authentication is enabled */
  isFirebaseEnabled?: boolean;
  /** Whether Azure authentication is enabled */
  isAzureEnabled?: boolean;
  /** Callback when Firebase login is requested */
  onFirebaseLogin?: () => void;
  /** Callback when Azure login is requested */
  onAzureLogin?: () => void;
  /** Callback when login is successful (for compatibility) */
  onLoginSuccess?: () => void;
  /** Additional CSS class name */
  className?: string;
  /** Custom styles */
  style?: React.CSSProperties;
}

export const LoginComponent: React.FC<LoginComponentProps> = ({
  title = "Login",
  logoUrl = "https://algotraders.ai/images/Logo.png",
  isFirebaseEnabled = false,
  isAzureEnabled = false,
  onFirebaseLogin,
  onAzureLogin,
  onLoginSuccess,
  className,
  style
}) => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  const signInWithGoogle = async () => {
    try {
      setError("");
      if (onFirebaseLogin) {
        onFirebaseLogin();
      }
    } catch (e) {
      console.error(e);
      setError("Google sign-in failed. Please try again.");
    }
  };

  const signInWithMSAL = async () => {
    try {
      setError("");
      if (onAzureLogin) {
        onAzureLogin();
      }
    } catch (e) {
      console.error(e);
      setError("Microsoft sign-in failed. Please try again.");
    }
  };

  // While checking auth state, show loading message
  if (loading) {
    return (
      <div className={`login-container ${className || ''}`} style={style}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            fontFamily: "Maven Pro, Arial, sans-serif",
          }}
        >
          <p style={{ fontSize: "16px", color: "#888" }}>Loading...</p>
        </div>
        <style>{loginStyles}</style>
      </div>
    );
  }

  return (
    <div className={`login-container ${className || ''}`} style={style}>
      <div className="login-card">
        <div className="login-prompt">
          <img src={logoUrl} alt="Logo" className="logo" />
          <h1>{title}</h1>
          <br />
          <br />
          <br />
          {isFirebaseEnabled && (
            <button onClick={signInWithGoogle} className="login-button">
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google Icon"
                className="provider-icon"
              />
              Sign In with Google
            </button>
          )}
          {isFirebaseEnabled && isAzureEnabled && <div style={{ height: 8 }} />}
          {isAzureEnabled && (
            <button onClick={signInWithMSAL} className="login-button">
              <img
                src="https://img.icons8.com/color/48/microsoft--v1.png"
                alt="Microsoft Icon"
                className="provider-icon"
              />
              Sign In with Microsoft
            </button>
          )}
          {error && <p className="error">{error}</p>}
        </div>
      </div>
      <style>{loginStyles}</style>
    </div>
  );
};

// CSS styles as a string to be injected
const loginStyles = `
  .login-container {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ddd;
    font-family: "Maven Pro", Arial, sans-serif;
    height: 100vh;
  }

  .login-container .login-card {
    background: #333;
    padding: 20px 30px;
    border: 1px solid #444;
    border-radius: 5px;
    text-align: center;
    min-width: 300px;
  }

  .login-container .login-card p {
    margin-bottom: 15px;
    font-size: 1.1em;
  }

  .login-container .login-card h1 {
    color: #ddd;
    font-family: "Maven Pro", Arial, sans-serif;
    font-weight: normal;
    margin: 0;
  }

  .login-container .login-button {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: #444 !important;
    color: #ddd !important;
    border: 1px solid #555 !important;
    padding: 10px 16px !important;
    width: 260px !important;
    cursor: pointer !important;
    font-size: 0.95em !important;
    transition: background 0.2s !important;
    font-family: "Maven Pro", Arial, sans-serif !important;
    text-decoration: none !important;
  }

  .login-container .login-button:hover {
    background: #555 !important;
  }

  .login-container .login-button:focus {
    outline: 2px solid #ddd !important;
    outline-offset: 2px !important;
  }

  .login-container .provider-icon {
    width: 24px !important;
    height: 24px !important;
    margin-right: 12px !important;
  }

  .login-container .logo {
    display: block !important;
    margin: 0 auto !important;
    width: 100px !important;
    margin-bottom: 20px !important;
  }

  .login-container .error {
    margin-top: 10px !important;
    font-size: 0.9em !important;
    color: #d32f2f !important;
  }
`;